import { describe_error } from '../utils/ErrorDescriber';
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Modal, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import SitesTable, { is_site_pre_processing, is_competitor_site } from '../components/SitesTable';
import SiteModal from './modals/SiteModal.js';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import { get_user_error_message } from '../utils/ErrorMessages';

// How often the site list is reloaded while any site's pages are being pre-processed.
const c_PreProcessingPollIntervalMs = 3000;


function SitesView(){
	const navigate = useNavigate();
	const { token } = useAuth();

	const [sites, set_sites] = useState(null);
	const [load_error, set_load_error] = useState('');

	// The pending reload while sites are pre-processed, and whether the view is still mounted, so a reload never
	// outlives the view.
	const poll_timeout_ref = useRef(null);
	const mounted_ref = useRef(true);

	// The site modal is open when show_site_modal is true; editing_site is null when adding a site.
	const [show_site_modal, set_show_site_modal] = useState(false);
	const [editing_site, set_editing_site] = useState(null);

	const [deleting_site, set_deleting_site] = useState(null);
	const [deleting, set_deleting] = useState(false);
	const [delete_error, set_delete_error] = useState('');

	const get_headers = ()=>{
		return { Authorization: 'Bearer ' + token };
	};

	const stop_polling = ()=>{
		if(poll_timeout_ref.current){
			clearTimeout(poll_timeout_ref.current);
			poll_timeout_ref.current = null;
		}
	};

	// Reloads the list again later while any site's pages are still being pre-processed, so the status column
	// follows the crawl without a refresh.
	const schedule_reload = (loaded_sites)=>{
		stop_polling();

		const pre_processing = loaded_sites.some((site)=>{ return is_site_pre_processing(site); });
		if(!pre_processing || !mounted_ref.current){
			return;
		}

		poll_timeout_ref.current = setTimeout(()=>{
			poll_timeout_ref.current = null;
			load_sites();
		}, c_PreProcessingPollIntervalMs);
	};

	const load_sites = async()=>{
		try {
			set_load_error('');
			const response = await axios.get(Constants.SERVER_URL + '/api/sites/', { headers: get_headers() });
			const loaded_sites = (response.data && response.data.sites) ? response.data.sites : [];
			if(!mounted_ref.current){
				return;
			}

			set_sites(loaded_sites);
			schedule_reload(loaded_sites);
		}
		catch (error) {
			console.log('SitesView/load_sites - ERROR: ', describe_error(error));
			if(!mounted_ref.current){
				return;
			}

			set_load_error(get_user_error_message(error, 'Your sites could not be loaded.'));
			schedule_retry();
		}
	};

	// After a failed load the list is loaded again later, so it recovers once the server answers.
	const schedule_retry = ()=>{
		stop_polling();
		poll_timeout_ref.current = setTimeout(()=>{
			poll_timeout_ref.current = null;
			load_sites();
		}, c_PreProcessingPollIntervalMs);
	};

	useEffect(()=>{
		mounted_ref.current = true;
		load_sites();

		return ()=>{
			mounted_ref.current = false;
			stop_polling();
		};
	}, [token]);

	const open_add_modal = ()=>{
		set_editing_site(null);
		set_show_site_modal(true);
	};

	const open_edit_modal = (site)=>{
		set_editing_site(site);
		set_show_site_modal(true);
	};

	const close_site_modal = ()=>{
		set_show_site_modal(false);
		set_editing_site(null);
	};

	// Called by SiteModal. Throws an Error carrying the message to show, so the modal stays open on a failure.
	const save_site = async(site_data)=>{
		try {
			if(editing_site){
				const payload = { name: site_data.name, description: site_data.description };
				await axios.put(Constants.SERVER_URL + '/api/site/' + editing_site.id, payload, { headers: get_headers() });
			}
			else {
				await axios.post(Constants.SERVER_URL + '/api/sites/', site_data, { headers: get_headers() });
			}
		}
		catch (error) {
			console.log('SitesView/save_site - ERROR: ', describe_error(error));
			throw new Error(get_user_error_message(error, 'The site could not be saved. Please try again.'));
		}

		close_site_modal();
		await load_sites();
	};

	const open_delete_modal = (site)=>{
		set_delete_error('');
		set_deleting_site(site);
	};

	const close_delete_modal = ()=>{
		set_deleting_site(null);
		set_delete_error('');
	};

	const delete_site = async()=>{
		try {
			set_deleting(true);
			set_delete_error('');
			await axios.delete(Constants.SERVER_URL + '/api/site/' + deleting_site.id, { headers: get_headers() });
			set_deleting(false);
			set_deleting_site(null);
			await load_sites();
		}
		catch (error) {
			console.log('SitesView/delete_site - ERROR: ', describe_error(error));
			set_delete_error(get_user_error_message(error, 'The site could not be deleted. Please try again.'));
			set_deleting(false);
		}
	};

	const open_site = (site)=>{
		navigate('/site/' + site.id + '/dashboard');
	};

	const render_site_modal = ()=>{
		if(!show_site_modal){
			return null;
		}

		return (
			<SiteModal
				site={editing_site}
				on_save={save_site}
				on_close={close_site_modal}/>
		);
	};

	// What deleting the site removes: a site with competitors takes them with it.
	const get_delete_text = ()=>{
		const text = 'Delete ' + deleting_site.title + '? Its pages and site analyses are removed. Your page analyses are kept.';
		const has_competitors = sites.some((site)=>{ return is_competitor_site(site) && (site.competitor_of_site_id === deleting_site.id); });
		if(!has_competitors){
			return text;
		}

		return text + ' Its competitors are deleted with it.';
	};

	const render_delete_modal = ()=>{
		if(!deleting_site){
			return null;
		}

		return (
			<Modal show={true} onHide={close_delete_modal} centered>
				<Modal.Header closeButton>
					<Modal.Title>Delete Site</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>
						{get_delete_text()}
					</p>
					{delete_error && <Alert variant="danger">{delete_error}</Alert>}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" disabled={deleting} onClick={close_delete_modal}>
						Cancel
					</Button>
					<Button variant="danger" disabled={deleting} onClick={delete_site}>
						{deleting ? <Spinner animation="border" size="sm"/> : 'Delete Site'}
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	const render_sites = ()=>{
		if(sites === null){
			return (
				<div className="sites-loading">
					<Spinner animation="border" size="sm"/>
				</div>
			);
		}

		if(sites.length == 0){
			return (
				<p className="sites-empty">No sites yet. Add a site to analyze its pages.</p>
			);
		}

		return (
			<SitesTable
				sites={sites}
				on_open={open_site}
				on_edit={open_edit_modal}
				on_delete={open_delete_modal}/>
		);
	};

	return (
		<React.Fragment>
			<Container className="view-container sites-view">
				<Row className="sites-view-header">
					<Col>
						<h1 className="sites-view-title">Sites</h1>
					</Col>
					<Col className="sites-view-header-actions">
						<Button variant="primary" onClick={open_add_modal}>
							Add Site
						</Button>
					</Col>
				</Row>

				{load_error && <Alert variant="danger">{load_error}</Alert>}

				<Row>
					<Col>
						{render_sites()}
					</Col>
				</Row>
			</Container>

			{render_site_modal()}
			{render_delete_modal()}
		</React.Fragment>
	);
}

export default SitesView;
